import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, Users, Calendar, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate } from "@/lib/api";

const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    if (!target) return { d: 0, h: 0, m: 0, s: 0 };
    let diff = Math.max(0, new Date(target).getTime() - now);
    const d = Math.floor(diff / 86400000); diff -= d * 86400000;
    const h = Math.floor(diff / 3600000); diff -= h * 3600000;
    const m = Math.floor(diff / 60000); diff -= m * 60000;
    const s = Math.floor(diff / 1000);
    return { d, h, m, s };
  }, [target, now]);
};

export default function LuckyDraw() {
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);

  const load = () => api.get("/customer/lucky-draws").then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);

  const join = async (id) => {
    try { await api.post(`/customer/lucky-draws/${id}/join`); toast.success("Joined group!"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="lucky-draw-page">
      <div className="bg-tenant-gradient text-white px-5 pt-6 pb-6 flex items-center gap-3">
        <button data-testid="header-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="font-extrabold tracking-tight text-lg">Lucky Draw Contest</div>
      </div>

      <div className="px-5 pt-6 space-y-4">
        {groups.map((g) => <GroupCard key={g.id} g={g} onJoin={() => join(g.id)} />)}
        {groups.length === 0 && <div className="text-center text-slate-400 text-sm py-12">No lucky draw groups available.</div>}

        <div className="bg-white border border-slate-100 rounded-2xl p-4 card-shadow">
          <h3 className="font-bold mb-2">How it Works</h3>
          <ol className="space-y-1.5 text-sm text-slate-600">
            <li>1. Complete 6 months premium payments</li>
            <li>2. 50 members form one group</li>
            <li>3. Random winner selection on draw date</li>
            <li>4. Winner gets <b className="text-emerald-600">50% cashback</b> to Bonus Wallet</li>
          </ol>
        </div>
      </div>
      <div className="h-10" />
    </div>
  );
}

const GroupCard = ({ g, onJoin }) => {
  const cd = useCountdown(g.draw_date);
  const percent = Math.round(((g.member_count || 0) / g.max_members) * 100);
  return (
    <div data-testid={`lucky-group-${g.id}`} className="bg-white border border-slate-100 rounded-3xl p-5 card-shadow">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Group</div>
          <div className="font-extrabold tracking-tight text-lg flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> {g.name}</div>
        </div>
        <span className={`text-[10px] uppercase font-bold px-3 py-1 rounded-full ${g.status === "open" ? "bg-emerald-50 text-emerald-700" : g.status === "locked" ? "bg-amber-50 text-amber-700" : "bg-violet-50 text-violet-700"}`}>{g.status}</span>
      </div>

      <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
        <Calendar className="w-3.5 h-3.5" /> Draw Date: <b className="text-slate-700">{fmtDate(g.draw_date)}</b>
      </div>

      {g.status === "completed" ? (
        <div className="mt-4 bonus-wallet-grad text-white rounded-2xl p-4 text-center">
          <div className="text-[10px] uppercase tracking-widest opacity-80">Winner</div>
          <div className="text-xl font-extrabold tracking-tight mt-1">{g.winner_name || "—"}</div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-4 gap-2 text-center">
          {[["Days", cd.d], ["Hrs", cd.h], ["Min", cd.m], ["Sec", cd.s]].map(([l, v]) => (
            <div key={l} className="bg-slate-50 rounded-xl py-2">
              <div className="text-xl font-extrabold tracking-tight">{String(v).padStart(2, "0")}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">{l}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs">
          <span className="font-bold flex items-center gap-1 text-slate-600"><Users className="w-3 h-3" /> Members</span>
          <span className="font-extrabold">{g.member_count || 0} / {g.max_members}</span>
        </div>
        <div className="mt-1.5 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bonus-wallet-grad" style={{ width: `${percent}%` }} />
        </div>
      </div>

      {g.status === "open" && !g.enrolled && (
        <button data-testid={`join-group-${g.id}`} onClick={onJoin} className="w-full mt-4 bg-tenant-primary text-white py-3 rounded-2xl font-bold tap-scale">
          Join Group
        </button>
      )}
      {g.enrolled && (
        <div className="mt-4 text-center text-emerald-600 font-bold inline-flex items-center justify-center gap-1.5 w-full">
          <CheckCircle2 className="w-4 h-4" /> You're in this group
        </div>
      )}
    </div>
  );
};
